import { GetObjectCommand, GetObjectCommandInput } from '@aws-sdk/client-s3';
import {
  PresignedPostOptions,
  createPresignedPost,
} from '@aws-sdk/s3-presigned-post';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { s3Client } from '@zorro/clients';
import {
  BUCKET_EXPIRY,
  EMPLOYER_IMAGES_BUCKET,
  extractFileKeyFromUrl,
  logger,
} from '@zorro/common-utils';
import axios from 'axios';
import type { NextApiRequest, NextApiResponse } from 'next';
import { v4 as uuidv4 } from 'uuid';

const handleCreatePresignedPost = async (
  req: NextApiRequest,
  res: NextApiResponse
) => {
  // INFO: use uuid in upload path to avoid overwriting files with the same filename
  const uploadKey = `${uuidv4()}/${req.query.file}`;
  const bucketParams: PresignedPostOptions = {
    Bucket: EMPLOYER_IMAGES_BUCKET,
    Key: uploadKey,
    Fields: {
      'Content-Type': req.query.fileType as string,
    },
    Expires: BUCKET_EXPIRY,
  };

  const data = await createPresignedPost(s3Client, bucketParams);
  res.status(200).json({ ...data });
};

const handleGetSignedUrl = async (
  req: NextApiRequest,
  res: NextApiResponse
) => {
  const key = req.query.key as string | undefined | null;

  if (!key) {
    res.status(500).json({ error: `Invalid key: ${key}` });
    return;
  }

  const bucketParams: GetObjectCommandInput = {
    Bucket: EMPLOYER_IMAGES_BUCKET,
    Key: key,
  };

  const command = new GetObjectCommand(bucketParams);
  const url = await getSignedUrl(s3Client, command, {
    expiresIn: BUCKET_EXPIRY,
  });
  res.status(200).json({ url });
};

export const employerLogosHandler = async (
  req: NextApiRequest,
  res: NextApiResponse
) => {
  try {
    const operationType = req.query.operationType;
    switch (operationType) {
      case 'createPresignedPost': {
        await handleCreatePresignedPost(req, res);
        break;
      }
      case 'getSignedUrl': {
        await handleGetSignedUrl(req, res);
        break;
      }
      default: {
        res
          .status(500)
          .json({ error: `Invalid operationType: ${operationType}` });
      }
    }
  } catch (error) {
    logger.error(error);

    res
      .status(500)
      .json({ error: `Failed to sign employer document operation: ${error}` });
  }
};

export const createEmployerLogoUrl = async (
  file: File
): Promise<{ logoUrl: string; fileName: string }> => {
  const fileName = encodeURIComponent(file.name.replaceAll(' ', '_'));
  const fileType = encodeURIComponent(file.type);

  const res = await axios.get(
    `/api/employer-logos?operationType=createPresignedPost&file=${fileName}&fileType=${fileType}`
  );

  const { url, fields } = await res.data;
  const formData = new FormData();

  Object.entries({ ...fields, file }).forEach(([key, value]) => {
    formData.append(key, value as string);
  });

  await axios.post(url, formData);
  return { logoUrl: url + fields.key, fileName };
};

export const getEmployerLogo = async (logoUrl: string): Promise<string> => {
  const key = extractFileKeyFromUrl(logoUrl);

  const { data } = await axios.get(
    `/api/employer-logos?operationType=getSignedUrl&key=${key}`
  );

  return data.url;
};
